import React, { createRef, RefObject } from 'react';
import { useSelector } from 'react-redux';
import { selectWizard } from '../../features/wizard/wizardSlice';
import { Route, Switch, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { RouteConfig } from './RouteWithSubRoutes';
import { usePrevious } from '../../utils/component-helpers';

export type CSSTransitionSubRoutesRouteProps = {
  routes: RouteConfig[];
  onMoveForward: (siteId?: number) => void;
};

//clones a react element to be used for react-transition-group
const dynamicChildFactory = (classNames: string, timeout: number) => (child: React.ReactElement) =>
  React.cloneElement(child, {
    classNames,
    timeout,
  });

const getKeyPathFromPathname = (pathname: string) => {
  return pathname.split('/')[3];
};

//timeout for sliding transition
const TRANSITION_TIMEOUT_MS = 300;

type RouteRefs = Record<string, RefObject<HTMLDivElement>>;

/**
 * Used to generate sliding routes with possible sub-routes.
 *
 * @param routes Configuration for the routes.
 * @param onMoveForward Function to move between routes.
 */
export const CSSTransitionSubRoutes = ({ routes, onMoveForward }: CSSTransitionSubRoutesRouteProps) => {
  //Create a Ref for every paths that need sliding transition
  const routeRefs: RouteRefs = {};
  routes.forEach((route) => {
    routeRefs[getKeyPathFromPathname(route.path)] = createRef();
  });

  const location = useLocation();
  const { pathname, key } = location;
  const paths = pathname.split('/');
  const currentRoute = paths[paths.length - 1];
  const wizard = useSelector(selectWizard);
  const isSteppingForward = wizard.direction === 1;
  const prevRoute = usePrevious(currentRoute) || '';
  const SUB_ROUTES = ['ct_configuration', 'switch_configuration', 'meter_tests'];
  const METER_TESTS_SUB_ROUTES = ['solar', 'grid', 'loads_list'];
  const isNavigatingFromSubRoute = currentRoute === 'meters' && SUB_ROUTES.includes(prevRoute);
  const isNavigatingFromMeterSubRoute = currentRoute === 'meter_tests' && METER_TESTS_SUB_ROUTES.includes(prevRoute);
  const isNavigatingFromLoadTest = currentRoute === 'loads_list' && prevRoute !== 'meter_tests';
  const isNavigatingToSubRoute = prevRoute === 'meters' && SUB_ROUTES.includes(currentRoute);
  const isNavigatingToMeterSubRoute = METER_TESTS_SUB_ROUTES.includes(currentRoute) && prevRoute === 'meter_tests';
  const isNavigatingToLoadsPage = currentRoute !== 'meter_tests' && prevRoute === 'loads_list';
  const shouldSlideBackward = isNavigatingFromSubRoute || isNavigatingFromMeterSubRoute || isNavigatingFromLoadTest;
  const shouldSlideForward = isNavigatingToSubRoute || isNavigatingToMeterSubRoute || isNavigatingToLoadsPage;
  let className = isSteppingForward || shouldSlideForward ? 'slide-forward' : 'slide-backward';
  if (shouldSlideBackward) className = 'slide-backward';
  if (!wizard.isSlidingEnabled) className = ''; // No sliding transition in initial load
  const nodeRef = routeRefs[getKeyPathFromPathname(location.pathname)];
  return (
    <TransitionGroup childFactory={dynamicChildFactory(className, TRANSITION_TIMEOUT_MS)}>
      <CSSTransition classNames={className} key={key} timeout={TRANSITION_TIMEOUT_MS} nodeRef={nodeRef} unmountOnExit>
        <div className={'slide-card-wrapper'} ref={nodeRef}>
          <Switch location={location}>
            {routes.map(({ path, component: Component, routes }) => (
              <Route path={path} key={path}>
                <Component onMoveForward={onMoveForward} routes={routes} />
              </Route>
            ))}
          </Switch>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};
